import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiAlertCircle,
  FiWifiOff,
  FiFileText,
  FiMapPin,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import LoadingScreen from '../../components/ui/LoadingScreen';
import EmptyState from '../../components/ui/EmptyState';
import { getHouseSurveys } from '../../services/apiService';
import { getHouses } from '../../services/houseService';

/**
 * Resuelve el valor crudo de una pregunta a su etiqueta legible.
 * La pregunta ya trae las opciones, settings y tipo inline desde la
 * API (`GET /v1/houses/:houseId/surveys`), así que no se necesita
 * buscar la definición del formulario por separado.
 */
function resolveAnswerLabel(question) {
  const rawValue = question.value;

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return '—';
  }

  // Boolean: usa true_label / false_label de question.settings
  if (question.type === 'boolean') {
    if (rawValue === true || rawValue === 'true' || rawValue === 1) {
      return question.settings?.true_label || 'Sí';
    }
    if (rawValue === false || rawValue === 'false' || rawValue === 0) {
      return question.settings?.false_label || 'No';
    }
  }

  // Radio / select / checkbox: mapea value → label de la opción
  if (question.options?.length) {
    if (Array.isArray(rawValue)) {
      return (
        rawValue
          .map((v) => {
            const opt = question.options.find((o) => String(o.value) === String(v));
            return opt?.label ?? v;
          })
          .join(', ') || '—'
      );
    }
    const opt = question.options.find((o) => String(o.value) === String(rawValue));
    return opt?.label ?? String(rawValue);
  }

  return String(rawValue);
}

/**
 * Vista de consulta de respuestas guardadas para una vivienda.
 *
 * Llama a `GET /v1/houses/:houseId/surveys` que devuelve un arreglo
 * de encuestas, cada una con:
 * - `survey` (slug, label, description)
 * - `response` (status: "completed" | "pending", timestamps)
 * - `questions[]` con `answered`, `value`, `is_visible`, `options`, etc.
 *
 * De este modo NO necesita hacer llamadas adicionales para obtener las
 * definiciones de las preguntas; todo viene inline.
 */
export default function HouseAnswersView() {
  const { houseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [sections, setSections] = useState([]);
  const [house, setHouse] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setOffline(!navigator.onLine);

    // Obtener datos de la vivienda para el header
    getHouses()
      .then((houses) => {
        if (!active) return;
        const found = houses?.find((h) => String(h.id) === String(houseId));
        if (found) setHouse(found);
      })
      .catch(() => {});

    // Obtener encuestas con respuestas
    getHouseSurveys(houseId)
      .then((res) => {
        if (!active) return;
        const surveysData = res?.data ?? [];

        const loadedSections = surveysData.map((entry) => {
          const survey = entry.survey ?? {};
          const questions = entry.questions ?? [];
          const response = entry.response ?? {};

          // Ordenar preguntas por `order`
          const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);

          // Preguntas visibles y contestadas
          const answeredItems = sortedQuestions
            .filter((q) => q.answered && q.value !== null && q.value !== undefined)
            .map((q) => ({
              label: q.label,
              value: resolveAnswerLabel(q),
            }));

          const totalAnswered = questions.filter((q) => q.answered).length;
          const totalVisible = questions.filter((q) => q.is_visible).length;

          return {
            slug: survey.slug,
            title: survey.label ?? survey.slug,
            status: response.status,
            items: answeredItems,
            answeredCount: totalAnswered,
            totalQuestions: totalVisible,
          };
        });

        setSections(loadedSections);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'No se pudieron cargar las respuestas.');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [houseId]);

  const houseAddress = house
    ? `${house.address.street} ${house.address.exteriorNumber}`.trim() +
      (house.address.neighborhood ? `, ${house.address.neighborhood}` : '')
    : null;

  if (loading) return <LoadingScreen message="Cargando respuestas…" />;

  return (
    <DashboardLayout
      title="Respuestas guardadas"
      subtitle={houseAddress || 'Resumen de formularios'}
    >
      {offline && !error && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión.
        </div>
      )}

      {error && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {error}
        </div>
      )}

      {houseAddress && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: '0.88rem',
            color: 'var(--pulso-text-muted)',
            padding: '8px 12px',
            background: 'var(--pulso-surface)',
            borderRadius: 'var(--pulso-radius-sm)',
            border: '1px solid var(--pulso-border)',
          }}
        >
          <FiMapPin style={{ color: 'var(--pulso-primary)', flexShrink: 0 }} />
          <span>
            Vivienda: <strong>{houseAddress}</strong>
          </span>
        </div>
      )}

      {sections.length === 0 && !error && (
        <EmptyState
          icon={<FiFileText />}
          title="Sin respuestas"
          description="No se han registrado respuestas para esta vivienda todavía."
        />
      )}

      {sections.map((section) => (
        <SectionCard
          key={section.slug}
          title={
            `${section.title} (${section.answeredCount}/${section.totalQuestions})`
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.85rem',
              fontWeight: 600,
              color: section.status === 'completed' ? '#16a34a' : '#d97706',
              marginBottom: section.items.length > 0 ? 8 : 0,
            }}
          >
            {section.status === 'completed' ? <FiCheckCircle /> : <FiClock />}
            {section.status === 'completed' ? 'Completado' : 'Pendiente'}
          </div>

          {section.items.length === 0 ? (
            <p className="empty-state__description">Sin respuestas en este formulario.</p>
          ) : (
            <div className="answers-list">
              {section.items.map((item, i) => (
                <div key={i} className="answer-row">
                  <span className="answer-row__label">{item.label}</span>
                  <span className="answer-row__value">{item.value}</span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      ))}

      <Link to="/data-entry/houses" className="btn btn-outline btn-block">
        <FiArrowLeft /> Volver a viviendas
      </Link>
    </DashboardLayout>
  );
}
